import type { Middleware, MiddlewareAPI } from 'redux';

// A dispatch link in the composed chain. Typed loosely on purpose: redux's generic `Dispatch<T>` fights
// middleware composition (each layer narrows the action) and adds no real safety to this plumbing.
type DispatchLink = (action: unknown) => unknown;
// A middleware already bound to the store api: takes the downstream link, returns this layer's link.
type Handler = (next: DispatchLink) => DispatchLink;

/**
 * Minimal dynamic middleware: lets the store append middleware after `createStore` (vanilla Redux can't).
 *
 * Used to attach RTK Query's middleware lazily on first feature use (see StoreManager.enableApi) so
 * RTK/RTKQ/immer stay off the eager boot bundle. Hand-rolled rather than RTK's own
 * `createDynamicMiddleware` precisely to avoid importing `@reduxjs/toolkit` eagerly — that pulls immer
 * into the eager chunk, which defeats the purpose.
 *
 * Each added middleware is bound to the store api exactly once (`mw(storeApi)`), so stateful middleware
 * like RTK Query — whose cache/subscription setup lives in that `(api) =>` layer — keeps its state. Only
 * the cheap `(next) =>` layer is re-linked when a middleware is added.
 */
export function createDynamicMiddleware() {
    const handlers: Handler[] = [];
    let storeApi: MiddlewareAPI | undefined;
    let rootNext: DispatchLink | undefined;
    let composed: DispatchLink | undefined;

    const recompose = () => {
        if (rootNext) {
            composed = handlers.reduceRight<DispatchLink>((next, handler) => handler(next), rootNext);
        }
    };

    const middleware: Middleware = api => (next: DispatchLink) => {
        storeApi = api;
        rootNext = next;
        recompose();
        // `composed` is set by recompose() above (rootNext is now defined); fall back to next defensively
        return action => (composed ?? next)(action);
    };

    const addMiddleware = (mw: Middleware) => {
        if (!storeApi) {
            throw new Error('dynamic middleware: addMiddleware called before the store was created');
        }
        handlers.push(mw(storeApi) as Handler);
        recompose();
    };

    return { middleware, addMiddleware };
}
